import { useState, useEffect, useCallback } from "react";
import { ChevronLeftFilled, ChevronRightFilled, DismissRegular } from "@fluentui/react-icons";
import styles from "./ImageModalCarousel.module.css";

interface ImageModalCarouselProps {
    images: string[];
    initialIndex?: number;
    isOpen: boolean;
    onClose: () => void;
    carTitle: string;
}

export const ImageModalCarousel = ({
    images,
    initialIndex = 0,
    isOpen,
    onClose,
    carTitle
}: ImageModalCarouselProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Update current index when initialIndex changes
    useEffect(() => {
        setCurrentIndex(initialIndex);
        setIsImageLoaded(false);
        setImageError(false);
    }, [initialIndex]);

    const handlePrevious = useCallback(() => {
        setIsImageLoaded(false);
        setImageError(false);
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    }, [images.length]);

    const handleNext = useCallback(() => {
        setIsImageLoaded(false);
        setImageError(false);
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }, [images.length]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case "Escape":
                    onClose();
                    break;
                case "ArrowLeft":
                    handlePrevious();
                    break;
                case "ArrowRight":
                    handleNext();
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose, handlePrevious, handleNext]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    if (!isOpen || images.length === 0) return null;

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={handleBackdropClick}>
            <div className={styles.modalContent}>
                {/* Close Button */}
                <button
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Cerrar"
                >
                    <DismissRegular />
                </button>

                {/* Image Counter */}
                <div className={styles.imageCounter}>
                    {currentIndex + 1} / {images.length}
                </div>

                {/* Car Title */}
                <div className={styles.carTitle}>
                    {carTitle}
                </div>

                {/* Main Image Container */}
                <div className={styles.imageContainer}>
                    {!imageError ? (
                        <>
                            <img
                                src={images[currentIndex]}
                                alt={`${carTitle} - Imagen ${currentIndex + 1}`}
                                className={`${styles.mainImage} ${isImageLoaded ? styles.loaded : ''}`}
                                onLoad={() => {
                                    setIsImageLoaded(true);
                                    setImageError(false);
                                }}
                                onError={() => {
                                    setIsImageLoaded(true);
                                    setImageError(true);
                                }}
                                draggable={false}
                            />

                            {!isImageLoaded && (
                                <div className={styles.imageLoader}>
                                    <div className={styles.spinner}></div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.imageErrorContainer}>
                            <div className={styles.imageErrorContent}>
                                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 7C5 5.89543 5.89543 5 7 5H17C18.1046 5 19 5.89543 19 7V17C19 18.1046 18.1046 19 17 19H7C5.89543 19 5 18.1046 5 17V7Z" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
                                    <path d="M12 14C13.6569 14 15 12.6569 15 11C15 9.34315 13.6569 8 12 8C10.3431 8 9 9.34315 9 11C9 12.6569 10.3431 14 12 14Z" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
                                    <path d="M5 15L8.5 11.5L11 14L14.5 10.5L19 15" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <div className={styles.imageErrorText}>Imagen no disponible</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation Buttons */}
                {images.length > 1 && (
                    <>
                        <button
                            className={`${styles.navButton} ${styles.prevButton}`}
                            onClick={handlePrevious}
                            aria-label="Imagen anterior"
                        >
                            <ChevronLeftFilled />
                        </button>
                        <button
                            className={`${styles.navButton} ${styles.nextButton}`}
                            onClick={handleNext}
                            aria-label="Imagen siguiente"
                        >
                            <ChevronRightFilled />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
